<template>
  <div class="rich-filters">
    <!-- Header row -->
    <div class="rf-head">
      <div class="rf-head-left">
        <div class="rf-head-icon"><filter-outlined /></div>
        <div>
          <div class="rf-head-title">Filters</div>
          <div class="rf-head-sub">
            {{ activeSummary }}
          </div>
        </div>
      </div>
      <div class="rf-head-actions">
        <a-button
          size="small"
          type="text"
          :disabled="!hasAppliedFilters"
          @click="handleReset"
        >
          <template #icon><undo-outlined /></template>
          Reset
        </a-button>
      </div>
    </div>

    <!-- Primary filter row -->
    <div class="rf-row rf-row--primary">
      <div class="rf-field">
        <label class="rf-label">
          <appstore-outlined class="rf-label-icon" />
          Operation
        </label>
        <a-select
          v-model:value="selectedTaskId"
          show-search
          class="rf-select"
          placeholder="Select an operation"
          :options="taskOptions"
          :filter-option="filterOption"
          :loading="loadingTasks"
          size="middle"
          allow-clear
        />
      </div>

      <div class="rf-field">
        <label class="rf-label">
          <team-outlined class="rf-label-icon" />
          Devices
          <span v-if="selectedDevices.length" class="rf-count-chip">
            {{ selectedDevices.length }}
          </span>
        </label>
        <a-select
          v-model:value="selectedDevices"
          mode="multiple"
          class="rf-select"
          placeholder="All devices"
          :options="deviceOptions"
          :filter-option="filterOption"
          :max-tag-count="1"
          size="middle"
          allow-clear
        />
      </div>

      <div class="rf-field rf-field--wide">
        <label class="rf-label">
          <calendar-outlined class="rf-label-icon" />
          Date range
        </label>
        <a-range-picker
          v-model:value="dateRange"
          :show-time="{ format: 'HH:mm' }"
          format="YYYY-MM-DD HH:mm"
          class="rf-datepicker"
          size="middle"
          :allow-clear="false"
          :ranges="rangeShortcuts"
        />
      </div>

      <div class="rf-primary-cta">
        <a-button
          type="primary"
          size="middle"
          class="rf-cta"
          :loading="loading"
          @click="handleUpdate"
        >
          <template #icon><sync-outlined /></template>
          Apply
        </a-button>
      </div>
    </div>

    <!-- Applied filter chips -->
    <div v-if="appliedChips.length" class="rf-chips-row">
      <span class="rf-chips-label">Applied:</span>
      <a-tag
        v-for="chip in appliedChips"
        :key="chip.key"
        closable
        color="blue"
        class="rf-chip"
        @close.prevent="removeChip(chip)"
      >
        <span class="rf-chip-key">{{ chip.label }}:</span>
        <span class="rf-chip-value">{{ chip.value }}</span>
      </a-tag>
    </div>

    <!-- Advanced (collapsible) -->
    <div class="rf-advanced-toggle" @click="showAdvanced = !showAdvanced">
      <right-outlined
        class="rf-caret"
        :class="{ 'rf-caret--open': showAdvanced }"
      />
      <span>Advanced filters</span>
      <span v-if="advancedAppliedCount" class="rf-advanced-badge">
        {{ advancedAppliedCount }} applied
      </span>
    </div>

    <transition name="rf-expand">
      <div v-if="showAdvanced" class="rf-advanced">
        <div class="rf-row">
          <div class="rf-field">
            <label class="rf-label">
              <check-square-outlined class="rf-label-icon" />
              Cycle type
            </label>
            <a-select
              v-model:value="cycleType"
              class="rf-select"
              size="middle"
              :options="cycleTypeOptions"
            />
          </div>

          <div class="rf-field">
            <label class="rf-label">
              <clock-circle-outlined class="rf-label-icon" />
              Cycle time (s)
            </label>
            <a-input-group compact class="rf-cycle-range">
              <a-input-number
                v-model:value="minCycleTime"
                :min="0"
                placeholder="Min"
                size="middle"
                class="rf-cycle-input"
              />
              <a-input-number
                v-model:value="maxCycleTime"
                :min="0"
                placeholder="Max"
                size="middle"
                class="rf-cycle-input"
              />
            </a-input-group>
          </div>

          <div class="rf-field rf-field--check">
            <a-checkbox v-model:checked="excludeTimeoutCycles">
              Exclude timed-out cycles
            </a-checkbox>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  RightOutlined,
  SyncOutlined,
  TeamOutlined,
  UndoOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { dateTimeFormat } from 'src/config/date-format-config';
import { getSortedTask } from 'src/utils/task';
import { mapActions, mapGetters } from 'vuex';

const CYCLE_TYPE_OPTIONS = [
  { label: 'All cycles', value: '' },
  { label: 'Good cycles', value: 'good_cycles' },
  { label: 'Bad cycles', value: 'bad_cycles' },
  { label: 'Timed-out cycles', value: 'timed_out_cycles' },
];

export default {
  name: 'RichFilters',
  components: {
    AppstoreOutlined,
    CalendarOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    FilterOutlined,
    RightOutlined,
    SyncOutlined,
    TeamOutlined,
    UndoOutlined,
  },
  props: {
    loading: { type: Boolean, default: false },
  },
  emits: ['change'],
  setup() {
    return {
      cycleTypeOptions: CYCLE_TYPE_OPTIONS,
    };
  },
  data() {
    return {
      selectedTaskId: null,
      selectedDevices: [],
      dateRange: [dayjs().subtract(1, 'day').startOf('hour'), dayjs()],
      cycleType: '',
      excludeTimeoutCycles: false,
      minCycleTime: null,
      maxCycleTime: null,
      showAdvanced: false,
      loadingTasks: false,
      hasApplied: false,
      hydratedFromUrl: false,
    };
  },
  computed: {
    ...mapGetters(['organization', 'allTasks', 'devices']),
    taskOptions() {
      return getSortedTask(this.allTasks || []).map((t) => ({
        label: t.taskName,
        value: t.id,
      }));
    },
    deviceOptions() {
      // Emit Serial_number as the option value so downstream fetches carry
      // the identifier the backend actually indexes by. display_name is
      // only for the label.
      return (this.devices || [])
        .filter((d) => !!d.Serial_number)
        .slice()
        .sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0))
        .map((d) => ({
          value: d.Serial_number,
          label:
            (d.starred ? '⭐ ' : '') +
            (d.display_name || d.Serial_number),
        }));
    },
    hasAppliedFilters() {
      return (
        this.selectedTaskId ||
        this.selectedDevices.length ||
        this.cycleType ||
        this.excludeTimeoutCycles ||
        this.minCycleTime != null ||
        this.maxCycleTime != null
      );
    },
    advancedAppliedCount() {
      let n = 0;
      if (this.cycleType) n += 1;
      if (this.excludeTimeoutCycles) n += 1;
      if (this.minCycleTime != null) n += 1;
      if (this.maxCycleTime != null) n += 1;
      return n;
    },
    appliedChips() {
      const chips = [];
      if (this.selectedTaskId) {
        chips.push({
          key: 'task',
          label: 'Operation',
          value: this.taskLabelFor(this.selectedTaskId),
        });
      }
      for (const serial of this.selectedDevices) {
        chips.push({
          key: `dev:${serial}`,
          label: 'Device',
          value: this.deviceLabelFor(serial),
          removable: 'device',
          serial,
        });
      }
      if (this.cycleType) {
        chips.push({
          key: 'ctype',
          label: 'Cycle type',
          value: CYCLE_TYPE_OPTIONS.find((o) => o.value === this.cycleType)?.label,
          removable: 'cycleType',
        });
      }
      if (this.excludeTimeoutCycles) {
        chips.push({
          key: 'no-timeout',
          label: 'Filter',
          value: 'Exclude timed-out cycles',
          removable: 'excludeTimeout',
        });
      }
      if (this.minCycleTime != null || this.maxCycleTime != null) {
        chips.push({
          key: 'cycle-range',
          label: 'Cycle time',
          value: `${this.minCycleTime ?? '—'}s – ${this.maxCycleTime ?? '—'}s`,
          removable: 'cycleRange',
        });
      }
      return chips;
    },
    rangeShortcuts() {
      const now = dayjs();
      return {
        'Last 24h': [now.subtract(24, 'hour'), now],
        'Last 7 days': [now.subtract(7, 'day'), now],
        'Last 30 days': [now.subtract(30, 'day'), now],
        Today: [now.startOf('day'), now],
      };
    },
    activeSummary() {
      if (!this.hasApplied) return 'Configure your filters and click Apply.';
      const count = this.appliedChips.length;
      const range = this.dateRange
        .map((d) => (d ? dayjs(d).format('MMM D, HH:mm') : ''))
        .join(' → ');
      return `${count} filter${count === 1 ? '' : 's'} · ${range}`;
    },
  },
  watch: {
    allTasks: {
      immediate: true,
      handler(list) {
        // Auto-select first task if none picked yet
        if (this.selectedTaskId || !list?.length) return;
        this.selectedTaskId = list[0].id;
      },
    },
  },
  async mounted() {
    // Hydrate any filter that came in via ?query so a refresh restores state.
    this.hydrateFromQuery();

    if (!this.allTasks?.length) {
      this.loadingTasks = true;
      try {
        await this.getAllTasks();
      } finally {
        this.loadingTasks = false;
      }
    }
    if (!this.devices?.length && this.organization) {
      await this.getOrgDevices(this.organization);
    }

    // If URL had a complete filter set, auto-apply so the ROI section
    // renders without the user having to click Apply again.
    if (this.hydratedFromUrl && this.canAutoApply()) {
      this.handleUpdate();
    }
  },
  methods: {
    ...mapActions(['getAllTasks', 'getOrgDevices']),

    filterOption(input, option) {
      const q = (input || '').toLowerCase();
      return String(option.label || '').toLowerCase().includes(q);
    },

    taskLabelFor(id) {
      return (
        this.taskOptions.find((o) => o.value === id)?.label || `#${id}`
      );
    },

    deviceLabelFor(serial) {
      return (
        this.deviceOptions.find((o) => o.value === serial)?.label || serial
      );
    },

    removeChip(chip) {
      switch (chip.removable) {
        case 'device':
          this.selectedDevices = this.selectedDevices.filter(
            (s) => s !== chip.serial
          );
          break;
        case 'cycleType':
          this.cycleType = '';
          break;
        case 'excludeTimeout':
          this.excludeTimeoutCycles = false;
          break;
        case 'cycleRange':
          this.minCycleTime = null;
          this.maxCycleTime = null;
          break;
        default:
      }
    },

    handleReset() {
      this.selectedDevices = [];
      this.cycleType = '';
      this.excludeTimeoutCycles = false;
      this.minCycleTime = null;
      this.maxCycleTime = null;
      this.hasApplied = false;
    },

    getDateTime(v) {
      return dayjs(v).format(dateTimeFormat);
    },

    handleUpdate() {
      const payload = {
        organization: this.organization,
        task: this.selectedTaskId,
        devices: JSON.stringify(this.selectedDevices),
        datetime_start: this.getDateTime(this.dateRange[0]),
        datetime_end: this.getDateTime(this.dateRange[1]),
        cycle_type: this.cycleType,
        exclude_timeout_cycles: this.excludeTimeoutCycles,
        ...(this.minCycleTime != null && { start_time: this.minCycleTime }),
        ...(this.maxCycleTime != null && { end_time: this.maxCycleTime }),
      };
      this.hasApplied = true;
      this.syncUrl(payload);
      this.$emit('change', payload);
    },

    canAutoApply() {
      return !!(
        this.selectedTaskId &&
        this.dateRange &&
        this.dateRange[0] &&
        this.dateRange[1]
      );
    },

    hydrateFromQuery() {
      const q = this.$route?.query || {};
      let touched = false;

      if (q.task) {
        const n = Number(q.task);
        if (Number.isFinite(n)) {
          this.selectedTaskId = n;
          touched = true;
        }
      }

      if (q.devices) {
        try {
          const parsed = JSON.parse(q.devices);
          if (Array.isArray(parsed)) {
            this.selectedDevices = parsed;
            touched = true;
          }
        } catch (e) {
          // ignore malformed devices
        }
      }

      if (q.datetime_start && q.datetime_end) {
        const start = dayjs(q.datetime_start);
        const end = dayjs(q.datetime_end);
        if (start.isValid() && end.isValid()) {
          this.dateRange = [start, end];
          touched = true;
        }
      }

      if (q.cycle_type) {
        this.cycleType = String(q.cycle_type);
        touched = true;
      }

      if (
        q.exclude_timeout_cycles === 'true' ||
        q.exclude_timeout_cycles === '1'
      ) {
        this.excludeTimeoutCycles = true;
        touched = true;
      }

      if (q.start_time != null && q.start_time !== '') {
        const n = Number(q.start_time);
        if (Number.isFinite(n)) {
          this.minCycleTime = n;
          touched = true;
        }
      }

      if (q.end_time != null && q.end_time !== '') {
        const n = Number(q.end_time);
        if (Number.isFinite(n)) {
          this.maxCycleTime = n;
          touched = true;
        }
      }

      // Expand advanced panel if any advanced field came in via URL
      if (
        q.cycle_type ||
        q.exclude_timeout_cycles ||
        q.start_time ||
        q.end_time
      ) {
        this.showAdvanced = true;
      }

      this.hydratedFromUrl = touched;
    },

    syncUrl(payload) {
      const nextQuery = { ...(this.$route?.query || {}) };
      if (payload.task) nextQuery.task = String(payload.task);
      else delete nextQuery.task;

      const parsedDevices = (() => {
        try {
          return JSON.parse(payload.devices);
        } catch (e) {
          return [];
        }
      })();
      if (Array.isArray(parsedDevices) && parsedDevices.length) {
        nextQuery.devices = JSON.stringify(parsedDevices);
      } else {
        delete nextQuery.devices;
      }

      if (payload.datetime_start) nextQuery.datetime_start = payload.datetime_start;
      else delete nextQuery.datetime_start;
      if (payload.datetime_end) nextQuery.datetime_end = payload.datetime_end;
      else delete nextQuery.datetime_end;

      if (payload.cycle_type) nextQuery.cycle_type = payload.cycle_type;
      else delete nextQuery.cycle_type;

      if (payload.exclude_timeout_cycles) {
        nextQuery.exclude_timeout_cycles = 'true';
      } else {
        delete nextQuery.exclude_timeout_cycles;
      }

      if (payload.start_time != null) {
        nextQuery.start_time = String(payload.start_time);
      } else {
        delete nextQuery.start_time;
      }
      if (payload.end_time != null) {
        nextQuery.end_time = String(payload.end_time);
      } else {
        delete nextQuery.end_time;
      }

      this.$router
        .replace({ path: this.$route.path, query: nextQuery })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.rich-filters {
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  padding: 12px 14px;
}

/* Header */
.rf-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.rf-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rf-head-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(24, 144, 255, 0.12);
  color: #1890ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.rf-head-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.rf-head-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}

/* Rows */
.rf-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  align-items: end;
}
.rf-row--primary {
  grid-template-columns: minmax(180px, 1.2fr) minmax(180px, 1fr) minmax(280px, 1.6fr) auto;
  align-items: end;
}
@media (max-width: 900px) {
  .rf-row--primary {
    grid-template-columns: 1fr 1fr;
  }
  .rf-primary-cta {
    grid-column: span 2;
    justify-self: end;
  }
}
@media (max-width: 560px) {
  .rf-row--primary {
    grid-template-columns: 1fr;
  }
  .rf-primary-cta {
    grid-column: auto;
    justify-self: stretch;
  }
  .rf-primary-cta .rf-cta {
    width: 100%;
  }
}

.rf-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.rf-field--check {
  justify-content: center;
  padding-top: 18px;
}
.rf-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
}
.rf-label-icon {
  font-size: 11px;
  color: #9ca3af;
}
.rf-count-chip {
  margin-left: 4px;
  background: #e6f7ff;
  color: #096dd9;
  border-radius: 999px;
  padding: 0 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.rf-select :deep(.ant-select-selector),
.rf-datepicker,
.rf-cycle-range {
  border-radius: 8px !important;
}
.rf-cycle-range {
  display: flex;
  gap: 6px;
}
.rf-cycle-input {
  width: 100% !important;
}
.rf-cycle-input :deep(.ant-input-number) {
  border-radius: 10px;
}

.rf-primary-cta {
  padding-top: 18px;
}
.rf-cta {
  border-radius: 8px;
  min-width: 96px;
}

/* Chips row */
.rf-chips-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #eef0f3;
}
.rf-chips-label {
  font-size: 11px;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-right: 4px;
}
.rf-chip {
  border-radius: 999px !important;
  padding: 2px 10px !important;
  font-size: 12px;
}
.rf-chip-key {
  color: #096dd9;
  font-weight: 700;
  margin-right: 4px;
}
.rf-chip-value {
  color: #111827;
}

/* Advanced */
.rf-advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;
}
.rf-advanced-toggle:hover {
  background: #f5f7fa;
}
.rf-caret {
  font-size: 10px;
  transition: transform 0.2s ease;
}
.rf-caret--open {
  transform: rotate(90deg);
}
.rf-advanced-badge {
  background: #e6f7ff;
  color: #096dd9;
  border-radius: 999px;
  padding: 0 7px;
  font-size: 10px;
  font-weight: 700;
}
.rf-advanced {
  margin-top: 6px;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #eef0f3;
}

/* Transition */
.rf-expand-enter-active,
.rf-expand-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  overflow: hidden;
}
.rf-expand-enter-from,
.rf-expand-leave-to {
  max-height: 0;
  opacity: 0;
}
.rf-expand-enter-to,
.rf-expand-leave-from {
  max-height: 300px;
  opacity: 1;
}
</style>
